/* ==========================================================================
   International Registry of Lethal Hands - Assessment
   Vanilla JS. No dependencies. State is client-side only.
   ========================================================================== */

(function () {
  'use strict';

  var PREFERS_REDUCED = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function init(root) {
    if (!root || root.dataset.lhReady === 'true') return;

    var dataEl = root.querySelector('[data-lh-config]');
    if (!dataEl) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }

    var questions = (config.questions || []).filter(function (q) {
      return q && q.answers && q.answers.length;
    });
    if (!questions.length) return;

    root.dataset.lhReady = 'true';

    var fadeMs = PREFERS_REDUCED.matches ? 0 : (config.fadeMs || 220);
    var stepMs = config.stepMs || 620;

    /* ---------- Element refs ---------- */

    var screens = {
      intro: root.querySelector('[data-lh-screen="intro"]'),
      assessment: root.querySelector('[data-lh-screen="assessment"]'),
      processing: root.querySelector('[data-lh-screen="processing"]'),
      results: root.querySelector('[data-lh-screen="results"]')
    };

    var questionText = root.querySelector('[data-lh-question]');
    var answersEl = root.querySelector('[data-lh-answers]');
    var progressCopy = root.querySelector('[data-lh-progress-copy]');
    var progressBar = root.querySelector('[data-lh-progress-bar]');
    var backBtn = root.querySelector('[data-lh-back]');
    var nextBtn = root.querySelector('[data-lh-next]');
    var beginBtn = root.querySelector('[data-lh-begin]');
    var processingCopy = root.querySelector('[data-lh-processing-copy]');
    var scoreValue = root.querySelector('[data-lh-score]');
    var resultTitle = root.querySelector('[data-lh-result-title]');
    var resultBody = root.querySelector('[data-lh-result-body]');
    var commendation = root.querySelector('[data-lh-commendation]');
    var resultActions = root.querySelector('[data-lh-result-actions]');
    var pending = root.querySelector('[data-lh-pending]');
    var certificate = root.querySelector('[data-lh-cert]');
    var proof = root.querySelector('[data-lh-proof]');

    if (!questionText || !answersEl || !nextBtn) return;

    /* ---------- State ---------- */

    var selections = new Array(questions.length).fill(null);
    var current = 0;
    var buttons = [];
    var processingTimer = null;

    /* ---------- Screen transitions ---------- */

    function reveal(el) {
      el.classList.add('is-active');
      void el.offsetWidth; // force reflow so the transition runs
      el.classList.add('is-visible');
    }

    function showScreen(name) {
      var next = screens[name];
      if (!next) return;

      var active = root.querySelector('.lh-screen.is-active');
      if (active === next) return;

      if (active) {
        active.classList.remove('is-visible');
        window.setTimeout(function () {
          active.classList.remove('is-active');
          reveal(next);
        }, fadeMs);
      } else {
        reveal(next);
      }

      if (root.getBoundingClientRect().top < 0) {
        root.scrollIntoView({
          behavior: PREFERS_REDUCED.matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    }

    /* ---------- Answer rendering ---------- */

    function paintSelection() {
      var chosen = selections[current];
      buttons.forEach(function (btn, i) {
        var on = chosen === i;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        btn.tabIndex = on || (chosen === null && i === 0) ? 0 : -1;
      });
      nextBtn.disabled = chosen === null;
    }

    function select(index) {
      selections[current] = index;
      paintSelection();
    }

    function moveFocus(from, delta) {
      var target = (from + delta + buttons.length) % buttons.length;
      select(target);
      buttons[target].focus();
    }

    function onAnswerKeydown(event) {
      var index = buttons.indexOf(event.currentTarget);
      if (index === -1) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          moveFocus(index, 1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          moveFocus(index, -1);
          break;
        case 'Home':
          event.preventDefault();
          moveFocus(-1, 1);
          break;
        case 'End':
          event.preventDefault();
          moveFocus(0, -1);
          break;
        default:
          break;
      }
    }

    function renderQuestion() {
      var question = questions[current];
      var isLast = current === questions.length - 1;

      if (progressCopy) {
        progressCopy.textContent = isLast
          ? config.finalQuestionLabel
          : config.questionLabel
              .replace(/\{\{\s*current\s*\}\}|\[current\]/g, current + 1)
              .replace(/\{\{\s*total\s*\}\}|\[total\]/g, questions.length);
      }

      if (progressBar) {
        progressBar.style.width = ((current + 1) / questions.length) * 100 + '%';
      }

      questionText.innerHTML = question.text;

      answersEl.innerHTML = '';
      buttons = question.answers.map(function (answer, index) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lh-answer';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.tabIndex = -1;

        var dot = document.createElement('span');
        dot.className = 'lh-radio';
        dot.setAttribute('aria-hidden', 'true');

        var label = document.createElement('span');
        label.className = 'lh-answer-label';
        label.textContent = answer.label;

        btn.appendChild(dot);
        btn.appendChild(label);

        btn.addEventListener('click', function () {
          select(index);
        });
        btn.addEventListener('keydown', onAnswerKeydown);

        answersEl.appendChild(btn);
        return btn;
      });

      if (backBtn) {
        backBtn.classList.toggle('lh-btn--hidden', current === 0);
        backBtn.disabled = current === 0;
      }

      nextBtn.textContent = isLast ? config.viewResultsLabel : config.continueLabel;
      paintSelection();
    }

    /* ---------- Scoring ---------- */

    function calculateScore() {
      return selections.reduce(function (total, answerIndex, questionIndex) {
        if (answerIndex === null) return total;
        var answer = questions[questionIndex].answers[answerIndex];
        return total + (answer ? answer.points : 0);
      }, 0);
    }

    function bandFor(score) {
      var bands = config.bands || [];
      for (var i = 0; i < bands.length; i++) {
        if (bands[i].max === null || score <= bands[i].max) return bands[i];
      }
      return bands[bands.length - 1];
    }

    /* ---------- Results ---------- */

    function makeButton(label, style) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lh-btn lh-btn--' + style;
      btn.textContent = label;
      return btn;
    }

    function makeLink(label, href, style) {
      var link = document.createElement('a');
      link.className = 'lh-btn lh-btn--' + style;
      link.href = href;
      link.textContent = label;
      return link;
    }

    function buildResults(score) {
      var band = bandFor(score);
      if (!band) return;

      if (scoreValue) scoreValue.textContent = score;
      if (resultTitle) resultTitle.textContent = band.title;
      if (resultBody) resultBody.innerHTML = band.body;

      if (commendation) {
        commendation.classList.toggle('is-shown', !!band.commendation);
      }

      /* The certificate, the pending label and the trust row are part of the
         offer, so they appear only when the visitor actually qualifies. */
      if (pending) pending.hidden = !band.eligible;
      if (certificate) certificate.classList.toggle('is-shown', !!band.eligible);
      if (proof) proof.classList.toggle('is-shown', !!band.eligible);

      if (!resultActions) return;
      resultActions.innerHTML = '';

      if (band.eligible) {
        if (config.registrationUrl) {
          resultActions.appendChild(
            makeLink(config.registerLabel, config.registrationUrl, 'primary')
          );
        }
        if (config.showRetakeOnEligible || !config.registrationUrl) {
          var retakeSecondary = makeButton(config.retakeLabel, 'secondary');
          retakeSecondary.addEventListener('click', resetAssessment);
          resultActions.appendChild(retakeSecondary);
        }
      } else {
        var retake = makeButton(config.retakeLabel, 'primary');
        retake.addEventListener('click', resetAssessment);
        resultActions.appendChild(retake);
      }

      var first = resultActions.querySelector('.lh-btn');
      if (first) {
        window.setTimeout(function () {
          first.focus({ preventScroll: true });
        }, fadeMs + 40);
      }
    }

    /* ---------- Processing ---------- */

    function runProcessing() {
      showScreen('processing');

      var steps = config.processingSteps || [];
      if (!steps.length) {
        buildResults(calculateScore());
        showScreen('results');
        return;
      }

      var index = 0;
      if (processingCopy) processingCopy.textContent = steps[index];

      window.clearInterval(processingTimer);
      processingTimer = window.setInterval(function () {
        index += 1;
        if (index < steps.length) {
          if (processingCopy) processingCopy.textContent = steps[index];
        } else {
          window.clearInterval(processingTimer);
          buildResults(calculateScore());
          showScreen('results');
        }
      }, stepMs);
    }

    /* ---------- Navigation ---------- */

    function resetAssessment() {
      window.clearInterval(processingTimer);
      current = 0;
      selections.fill(null);
      renderQuestion();
      showScreen('intro');
      if (beginBtn) {
        window.setTimeout(function () {
          beginBtn.focus({ preventScroll: true });
        }, fadeMs + 40);
      }
    }

    function focusQuestion() {
      window.setTimeout(function () {
        questionText.focus({ preventScroll: true });
      }, fadeMs + 40);
    }

    if (beginBtn) {
      beginBtn.addEventListener('click', function () {
        var certImg = certificate ? certificate.querySelector('img') : null;
        if (certImg) certImg.loading = 'eager';
        renderQuestion();
        showScreen('assessment');
        focusQuestion();
      });
    }

    nextBtn.addEventListener('click', function () {
      if (selections[current] === null) return;
      if (current < questions.length - 1) {
        current += 1;
        renderQuestion();
        focusQuestion();
      } else {
        runProcessing();
      }
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (current === 0) return;
        current -= 1;
        renderQuestion();
        focusQuestion();
      });
    }

    /* ---------- Boot ---------- */

    renderQuestion();

    var startScreen = root.dataset.lhStartScreen || 'intro';
    if (!screens[startScreen]) startScreen = 'intro';
    if (startScreen === 'results') {
      /* Editor preview: pick the highest-scoring answer on each question,
         so the preview always shows the best possible (eligible) result. */
      selections = questions.map(function (q) {
        var best = 0;
        q.answers.forEach(function (a, i) {
          if (a.points > q.answers[best].points) best = i;
        });
        return best;
      });
      buildResults(calculateScore());
    }
    reveal(screens[startScreen]);
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-lh-root]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  /* Shopify theme editor: re-init when the section is reloaded. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
